/**
 * AES-GCM + RSA-OAEP(SHA-256) Hybrid Encryption
 */
import {
  cryptoLog,
  generateAesKey,
  convertBufferToBase64,
  importRsaPublicKey,
} from "./keyUtils.js";

const publicKeyPem = process.env.REACT_APP_BACKEND_PUBLIC_KEY;

export async function hybridEncrypt(payload) {
  cryptoLog("hybridEncrypt:start");

  try {
    if (!publicKeyPem) throw new Error("Public key missing.");

    // 1️⃣ Generate AES-GCM key and IV (12 bytes recommended for GCM)
    const aesKey = await generateAesKey(256);
    const iv = window.crypto.getRandomValues(new Uint8Array(12));

    // 2️⃣ Encrypt payload using AES-GCM
    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify(payload));

    const encrypted = await window.crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      aesKey,
      data
    );

    // 3️⃣ Export raw AES key and encrypt it using RSA-OAEP(SHA-256)
    const rawKey = await window.crypto.subtle.exportKey("raw", aesKey);
    const publicKey = await importRsaPublicKey(publicKeyPem, "SHA-256");

    const encryptedKey = await window.crypto.subtle.encrypt(
      { name: "RSA-OAEP" },
      publicKey,
      rawKey
    );

    // 4️⃣ Separate ciphertext and tag
    const encryptedBytes = new Uint8Array(encrypted);
    const tagLength = 16;
    const ciphertext = encryptedBytes.slice(0, -tagLength);
    const tag = encryptedBytes.slice(-tagLength);

    // 5️⃣ Return base64-encoded result
    const result = {
      encrypted_key: convertBufferToBase64(encryptedKey),
      iv: convertBufferToBase64(iv),
      ciphertext: convertBufferToBase64(ciphertext),
      tag: convertBufferToBase64(tag),
    };

    cryptoLog("hybridEncrypt:success");
    return result;
  } catch (err) {
    cryptoLog("hybridEncrypt:error", err.message, "error");
    throw err;
  }
}
